// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { WebAuthnError } from "@simplewebauthn/browser";
import { KeyRound, Loader2, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { usePasskeyCredentialsQuery, useRegisterPasskeyMutation } from "@/api/hooks/use-passkeys";
import { KeyPlusIcon } from "@/components/icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { AAGUID_NAMES } from "@/lib/auth/aaguid-names";
import type { PasskeyCredential } from "@/lib/auth/types";
import { formatDate } from "@/lib/format";
import { useDateFormat } from "@/lib/preferences";
import { RevokePasskeyDialog } from "./revoke-passkey-dialog";

/**
 * The passkey list + register/revoke controls, without any surrounding chrome.
 * Rendered inline on the profile Security tab.
 */
export function PasskeyManager() {
  const { t } = useTranslation("back-office");
  const { data: credentials, isLoading, isError } = usePasskeyCredentialsQuery();
  const register = useRegisterPasskeyMutation();

  const [name, setName] = useState("");
  const [revoking, setRevoking] = useState<PasskeyCredential | null>(null);

  async function onAdd() {
    const friendlyName = name.trim();
    if (!friendlyName) return;
    try {
      await register.mutateAsync(friendlyName);
      setName("");
      toast.success(t("passkeys.registered"));
    } catch (err) {
      // A cancelled ceremony isn't an error worth surfacing.
      if (!(err instanceof WebAuthnError && err.code === "ERROR_CEREMONY_ABORTED")) {
        toast.error(t("passkeys.error"));
      }
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {/* List */}
      {isLoading ? (
        <Skeleton className="h-14 w-full" />
      ) : isError ? (
        // Don't fall through to "No passkeys yet" on a load failure — that misrepresents the account's
        // security state as "no passkeys" when the list simply didn't load. Surface the error. CIN-676.
        <p className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-center text-sm text-destructive">
          {t("errors.loadFailed")}
        </p>
      ) : credentials && credentials.length > 0 ? (
        <ul className="flex flex-col divide-y rounded-md border">
          {credentials.map((cred) => (
            <PasskeyRow key={cred.id} credential={cred} onRevoke={() => setRevoking(cred)} />
          ))}
        </ul>
      ) : (
        <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
          {t("passkeys.empty")}
        </p>
      )}

      {/* Add */}
      <form
        className="flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          onAdd();
        }}
      >
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("passkeys.namePlaceholder")}
          disabled={register.isPending}
        />
        <Button type="submit" disabled={register.isPending || !name.trim()} className="shrink-0 gap-2">
          {register.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyPlusIcon className="h-4 w-4" />}
          {register.isPending ? t("passkeys.adding") : t("passkeys.add")}
        </Button>
      </form>

      <RevokePasskeyDialog credential={revoking} onClose={() => setRevoking(null)} />
    </div>
  );
}

interface PasskeyRowProps {
  credential: PasskeyCredential;
  onRevoke: () => void;
}

function PasskeyRow({ credential, onRevoke }: PasskeyRowProps) {
  const { t } = useTranslation("back-office");
  const [dateFormat] = useDateFormat();

  // Prefer the resolved authenticator/provider name (iCloud Keychain, 1Password, …); fall back to the
  // synced/this-device label when the AAGUID is unknown (e.g. the all-zero AAGUID of synced passkeys).
  // Some catalog names are very long — cap the badge at 32 chars with an ellipsis.
  const rawProviderName = credential.aaguid ? AAGUID_NAMES[credential.aaguid] : undefined;
  const providerName =
    rawProviderName && rawProviderName.length > 32 ? `${rawProviderName.slice(0, 32).trimEnd()}…` : rawProviderName;
  const badgeLabel =
    providerName ??
    (credential.deviceType === "multi_device"
      ? t("passkeys.deviceType.multi_device")
      : credential.deviceType === "single_device"
        ? t("passkeys.deviceType.single_device")
        : null);

  return (
    <li className="flex items-center gap-3 p-3">
      <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{credential.friendlyName || t("passkeys.title")}</span>
          {badgeLabel && (
            <Badge variant="secondary" className="-mt-px shrink-0 text-[10px]">
              {badgeLabel}
            </Badge>
          )}
        </div>
        <p className="truncate text-xs text-muted-foreground">
          {t("passkeys.added", { date: formatDate(credential.createdAt, dateFormat) })}
          {" · "}
          {credential.lastUsedAt
            ? t("passkeys.lastUsed", { date: formatDate(credential.lastUsedAt, dateFormat) })
            : t("passkeys.neverUsed")}
        </p>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="shrink-0 text-destructive hover:text-destructive"
        onClick={onRevoke}
        aria-label={t("passkeys.revoke")}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </li>
  );
}
